import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  downloadBackupCollection,
  fetchBackupCollections,
  finalBackupRestore,
  previewBackupRestore,
} from '../services/adminBackupService';
import '../styles/adminBackups.css';

const defaultRestoreState = {
  file: null,
  restoreMode: 'upsert',
  confirmation: '',
  preview: null,
  inputKey: 0,
  isPreviewing: false,
  isRestoring: false,
};

function formatCount(value) {
  return new Intl.NumberFormat('en-IN').format(Number(value) || 0);
}

function getRestoreModeLabel(value) {
  return value === 'replace' ? 'Replace collection' : 'Upsert / Merge by _id';
}

function AdminBackups() {
  const [collections, setCollections] = useState([]);
  const [summary, setSummary] = useState({ totalCollections: 0, totalDocuments: 0 });
  const [restoreState, setRestoreState] = useState({});
  const [status, setStatus] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(true);

  const collectionNames = useMemo(() => collections.map((collection) => collection.name), [collections]);

  const getRowState = (collectionName) => restoreState[collectionName] || defaultRestoreState;

  const updateRowState = (collectionName, updates) => {
    setRestoreState((current) => ({
      ...current,
      [collectionName]: {
        ...defaultRestoreState,
        ...current[collectionName],
        ...updates,
      },
    }));
  };

  const resetRowState = (collectionName) => {
    setRestoreState((current) => {
      const previous = current[collectionName] || defaultRestoreState;

      return {
        ...current,
        [collectionName]: {
          ...defaultRestoreState,
          inputKey: previous.inputKey + 1,
        },
      };
    });
  };

  const loadCollections = useCallback(async () => {
    setIsLoading(true);
    setStatus({ type: '', text: '' });

    try {
      const data = await fetchBackupCollections();
      setCollections(data.collections);
      setSummary({
        totalCollections: data.totalCollections,
        totalDocuments: data.totalDocuments,
      });
      setStatus({ type: 'success', text: 'Collections loaded.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const handleFileChange = (collectionName, file) => {
    updateRowState(collectionName, {
      file: file || null,
      confirmation: '',
      preview: null,
    });
  };

  const handleRestoreModeChange = (collectionName, restoreMode) => {
    updateRowState(collectionName, {
      restoreMode,
      confirmation: '',
      preview: null,
    });
  };

  const handleDownload = async (collectionName) => {
    try {
      const filename = await downloadBackupCollection(collectionName);
      setStatus({ type: 'success', text: `Backup downloaded: ${filename}` });
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    }
  };

  const handlePreview = async (collectionName) => {
    const row = getRowState(collectionName);

    if (!row.file) {
      setStatus({ type: 'error', text: 'Choose a JSON backup file first.' });
      return;
    }

    updateRowState(collectionName, { isPreviewing: true, preview: null, confirmation: '' });

    try {
      const preview = await previewBackupRestore(collectionName, row.file, row.restoreMode);
      updateRowState(collectionName, { preview, isPreviewing: false });
      setStatus({ type: 'success', text: `Restore preview ready for ${collectionName}.` });
    } catch (error) {
      updateRowState(collectionName, { preview: null, isPreviewing: false });
      setStatus({ type: 'error', text: error.message });
    }
  };

  const handleFinalRestore = async (collectionName) => {
    const row = getRowState(collectionName);
    const confirmMessage =
      row.restoreMode === 'replace'
        ? `Replace mode will delete existing documents in ${collectionName} before insert. Continue?`
        : `Restore backup documents into ${collectionName} using upsert/merge mode?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    updateRowState(collectionName, { isRestoring: true });

    try {
      const restored = await finalBackupRestore(
        collectionName,
        row.file,
        row.restoreMode,
        row.confirmation,
        row.preview?.previewToken
      );

      resetRowState(collectionName);
      await loadCollections();
      setStatus({
        type: 'success',
        text: `Restore complete for ${collectionName}: ${formatCount(restored.inserted)} inserted, ${formatCount(
          restored.updated
        )} updated, ${formatCount(restored.deletedBeforeInsert)} deleted before insert.`,
      });
    } catch (error) {
      updateRowState(collectionName, { isRestoring: false });
      setStatus({ type: 'error', text: error.message });
    }
  };

  return (
    <section className="admin-page admin-backups-page">
      <div className="admin-page__header admin-page__header--split">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>DATABASE BACKUPS</h1>
          <p className="admin-backups-subtitle">Download and restore MongoDB collection JSON backups.</p>
        </div>
        <button className="btn btn--secondary" disabled={isLoading} onClick={loadCollections} type="button">
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <article className="admin-panel admin-backups-intro">
        <div>
          <p className="eyebrow">Backups</p>
          <h2>Collection-level JSON backup safety</h2>
          <p>
            Use this admin-only tool for collection-level JSON backups. Preview every restore first. Replace mode
            deletes the target collection before inserting backup documents.
          </p>
          <p className="admin-backups-note">Uploaded files should be backed up separately from server/uploads.</p>
        </div>
      </article>

      {status.text ? <p className={`form-alert form-alert--${status.type}`}>{status.text}</p> : null}

      <article className="admin-panel admin-backups-collections">
        <div className="admin-backups-heading">
          <div>
            <p className="eyebrow">Collections</p>
            <h2>MongoDB Collections</h2>
          </div>
          <strong>
            {formatCount(summary.totalCollections)} collections / {formatCount(summary.totalDocuments)} documents
          </strong>
        </div>

        {isLoading ? <p className="admin-empty">Loading collections...</p> : null}

        {!isLoading && collectionNames.length === 0 ? (
          <p className="admin-empty">No backup-safe collections were found.</p>
        ) : null}

        {collectionNames.length > 0 ? (
          <div className="admin-backups-table">
            <div className="admin-backups-table__head">
              <span>Collection</span>
              <span>Documents</span>
              <span>Download</span>
              <span>Restore</span>
            </div>

            {collections.map((collection) => {
              const row = getRowState(collection.name);
              const canRestore =
                row.file &&
                row.preview?.previewToken &&
                row.preview.restoreMode === row.restoreMode &&
                row.confirmation === 'RESTORE' &&
                !row.isPreviewing &&
                !row.isRestoring;

              return (
                <article className="admin-backups-row" key={collection.name}>
                  <div className="admin-backups-name">
                    <strong>{collection.name}</strong>
                  </div>
                  <div className="admin-backups-count">{formatCount(collection.count)}</div>
                  <div>
                    <button className="btn btn--primary" onClick={() => handleDownload(collection.name)} type="button">
                      Download JSON
                    </button>
                  </div>
                  <div className="admin-backups-restore">
                    <input
                      accept=".json,application/json"
                      key={row.inputKey}
                      onChange={(event) => handleFileChange(collection.name, event.target.files?.[0])}
                      type="file"
                    />

                    <button
                      className="admin-backups-action"
                      disabled={!row.file || row.isPreviewing}
                      onClick={() => handlePreview(collection.name)}
                      type="button"
                    >
                      {row.isPreviewing ? 'Previewing...' : 'Preview Restore'}
                    </button>

                    <div className="admin-backups-controls">
                      <label>
                        Restore mode
                        <select
                          onChange={(event) => handleRestoreModeChange(collection.name, event.target.value)}
                          value={row.restoreMode}
                        >
                          <option value="upsert">Upsert / Merge by _id</option>
                          <option value="replace">Replace collection</option>
                        </select>
                      </label>
                      <label>
                        Type RESTORE
                        <input
                          onChange={(event) => updateRowState(collection.name, { confirmation: event.target.value })}
                          placeholder="Type RESTORE"
                          value={row.confirmation}
                        />
                      </label>
                    </div>

                    {row.preview ? (
                      <div className="admin-backups-preview">
                        <span>{formatCount(row.preview.documentCount)} documents in backup file</span>
                        <span>{formatCount(row.preview.currentDocumentCount)} current documents</span>
                        <span>{getRestoreModeLabel(row.preview.restoreMode)}</span>
                        <span>{formatCount(row.preview.documentsWithId)} with _id</span>
                        <span>{formatCount(row.preview.documentsWithoutId)} without _id</span>
                        {row.preview.invalidIdCount ? (
                          <span>{formatCount(row.preview.invalidIdCount)} invalid _id values</span>
                        ) : null}
                        {row.preview.warnings?.map((warning) => (
                          <strong key={warning}>{warning}</strong>
                        ))}
                      </div>
                    ) : null}

                    {row.restoreMode === 'replace' ? (
                      <p className="admin-backups-danger">
                        Replace collection deletes all current documents before inserting the backup file.
                      </p>
                    ) : null}

                    <button
                      className="admin-backups-action admin-backups-action--final"
                      disabled={!canRestore}
                      onClick={() => handleFinalRestore(collection.name)}
                      type="button"
                    >
                      {row.isRestoring ? 'Restoring...' : 'Final Restore'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </article>
    </section>
  );
}

export default AdminBackups;
